import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { MANIFEST_SCREENS, canSeeScreen } from './accounting/manifests/ManifestsWorkspace';

const DepartmentPanel = ({ activeDepartment, onDepartmentChange, accountingScreen = null, onAccountingScreenChange = () => {} }) => {
  const { user } = useAuth();
  const departments = [
    {
      id: 'dispatch',
      label: 'Dispatch Operations',
      icon: '🚚',
      description: 'Route planning, load assignment, driver dispatch'
    },
    {
      id: 'accounting',
      label: 'Accounting',
      icon: '💰',
      description: 'Invoicing, payments, financial reporting',
      children: MANIFEST_SCREENS.filter((s) => canSeeScreen(s, user?.role))
    },
    {
      id: 'sales',
      label: 'Sales/Business Development',
      icon: '📈',
      description: 'Lead generation, CRM, rate quotes'
    },
    {
      id: 'hr',
      label: 'HR',
      icon: '👥',
      description: 'Recruitment, training, employee management'
    },
    {
      id: 'maintenance',
      label: 'Fleet Maintenance',
      icon: '🔧',
      description: 'Preventive maintenance, repairs, inspections'
    },
    {
      id: 'safety',
      label: 'Fleet Safety',
      icon: '🛡️',
      description: 'Safety compliance, accident prevention, training'
    }
  ];

  const [expanded, setExpanded] = useState(activeDepartment === 'accounting');

  useEffect(() => {
    if (activeDepartment === 'accounting') setExpanded(true);
  }, [activeDepartment]);

  const handleDeptClick = (dept) => {
    if (dept.children) {
      // Clicking the parent opens the main Accounting workspace and toggles the dropdown.
      if (activeDepartment === dept.id && !accountingScreen) setExpanded((v) => !v);
      else setExpanded(true);
      onAccountingScreenChange(null);
    }
    onDepartmentChange(dept.id);
  };

  return (
    <div className="w-full h-full bg-sidebar border-r border-sidebar-border flex flex-col">
      {/* Header */}
      <div className="p-5 border-b border-sidebar-border">
        <h2 className="font-bold text-base text-sidebar-foreground">Departments</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Select workspace</p>
      </div>

      {/* Department List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {departments.map((dept) => {
          const isActive = activeDepartment === dept.id;
          const parentHighlighted = isActive && !(dept.children && accountingScreen);
          return (
            <div key={dept.id}>
              <button
                id={`dept-${dept.id}`}
                onClick={() => handleDeptClick(dept)}
                aria-expanded={dept.children ? expanded : undefined}
                className={`w-full text-center p-4 transition-all relative ${
                  parentHighlighted
                    ? 'bg-primary text-primary-foreground'
                    : isActive
                    ? 'bg-accent text-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-accent'
                }`}
              >
                <div className="font-bold text-lg">
                  {dept.label}
                  {dept.children && (
                    <i className={`fas fa-chevron-down text-xs ml-2 transition-transform ${expanded ? 'rotate-180' : ''}`}></i>
                  )}
                </div>
                <div className={`text-xs mt-1 ${
                  parentHighlighted ? 'text-primary-foreground/80' : 'text-muted-foreground'
                }`}>
                  {dept.description}
                </div>
              </button>

              {dept.children && expanded && (
                <ul className="mt-1 space-y-0.5 pl-2 border-l-2 border-sidebar-border ml-3">
                  {dept.children.map((child) => {
                    const childActive = isActive && accountingScreen === child.key;
                    return (
                      <li key={child.key}>
                        <button
                          id={`accounting-nav-${child.key}`}
                          onClick={() => {
                            onDepartmentChange(dept.id);
                            onAccountingScreenChange(child.key);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors rounded-md ${
                            childActive
                              ? 'bg-primary text-primary-foreground font-semibold'
                              : 'text-sidebar-foreground hover:bg-accent'
                          }`}
                        >
                          <i className={`fas ${child.icon} w-4 text-center text-xs`}></i>
                          {child.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Info */}
      <div className="p-4 border-t border-sidebar-border bg-sidebar">
        <div className="text-xs text-muted-foreground text-center">
          <div className="font-semibold mb-1 text-sidebar-foreground">🤖 AI Assistant</div>
          <div>Context-aware help</div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentPanel;
